import { FC } from 'react';

import { SearchIcon, XIcon } from '@heroicons/react/outline';
import {
  EMPTY_GIFT_STORE_FILTER,
  GiftStoreFilter,
  isGiftStoreFilterActive,
  toggleGiftFilterKey,
} from '@shared/utils/giftStoreFilter.util';

export type GiftStoreFilterOption = {
  key: string;
  label: string;
  count: number;
};

interface GiftStoreFilterPanelProps {
  filter: GiftStoreFilter;
  onChange: (next: GiftStoreFilter) => void;
  onClose: () => void;
  priceOptions: GiftStoreFilterOption[];
  resultCount: number;
}

const chipClass = (isActive: boolean) =>
  `flex items-center gap-1 rounded-full px-3.5 py-1.5 text-13 font-semibold transition-colors ${
    isActive ? 'text-white theme-bg-main' : 'bg-gray-100 text-gray-600'
  }`;

/**
 * 스토어 메인 상세검색 패널 (검색어 · 가격대).
 * 고르는 즉시 목록에 반영되고, 닫아도 조건은 유지된다.
 */
export const GiftStoreFilterPanel: FC<GiftStoreFilterPanelProps> =
  function GiftStoreFilterPanel({
    filter,
    onChange,
    onClose,
    priceOptions,
    resultCount,
  }) {
    const isActive = isGiftStoreFilterActive(filter);

    const renderOptions = (
      options: GiftStoreFilterOption[],
      selected: string[],
      onToggle: (key: string) => void,
    ) => (
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const on = selected.includes(option.key);
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(option.key)}
              className={chipClass(on)}
            >
              {option.label}
              <span
                className={`text-xs font-normal ${on ? 'text-white/80' : 'text-gray-400'}`}
              >
                {option.count}
              </span>
            </button>
          );
        })}
      </div>
    );

    return (
      <div className="mt-2 overflow-y-auto rounded-2xl bg-white p-4 shadow-lg ring-1 ring-gray-200 max-h-[70vh]">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            enterKeyHint="search"
            value={filter.keyword}
            onChange={(e) => onChange({ ...filter, keyword: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
                onClose();
              }
            }}
            placeholder="상품명·브랜드·분류 검색"
            aria-label="상품 검색"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-10 text-base text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none focus:ring-0"
          />
          {filter.keyword && (
            <button
              type="button"
              aria-label="검색어 지우기"
              onClick={() => onChange({ ...filter, keyword: '' })}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {priceOptions.length > 1 && (
          <div className="mt-4">
            <p className="mb-2 text-13 font-semibold text-gray-900">가격대</p>
            {renderOptions(priceOptions, filter.priceKeys, (key) =>
              onChange({ ...filter, priceKeys: toggleGiftFilterKey(filter.priceKeys, key) }),
            )}
          </div>
        )}

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            disabled={!isActive}
            onClick={() => onChange(EMPTY_GIFT_STORE_FILTER)}
            className="shrink-0 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-600 disabled:text-gray-300"
          >
            초기화
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl px-4 py-3 text-sm font-semibold text-white theme-bg-main"
          >
            {isActive ? `상품 ${resultCount}개 보기` : '닫기'}
          </button>
        </div>
      </div>
    );
  };
